/**
 * ProcedureMultiSelect.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Seleção de um ou mais procedimentos para o agendamento, no visual dos campos
 * do AppointmentDrawer:
 *
 *  - o campo abre uma lista com caixas de marcação (busca quando há mais de
 *    8 procedimentos); os escolhidos aparecem como etiquetas com "x";
 *  - abaixo, uma linha por procedimento escolhido com a duração (min) e o
 *    valor, já preenchidos pelo cadastro e editáveis.
 *
 * Controlado pelo pai: `value` são as linhas ({ procedure_id, minutes, price })
 * e `onChange` recebe a lista nova. Conversões em procedureLines.js.
 *
 * @module ProcedureMultiSelect
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { formatPrice } from '../../utils/appointmentUtils';
import { lineFromProcedure, linesTotals } from './procedureLines';
import styles from './ProcedureMultiSelect.module.css';

const SEARCH_THRESHOLD = 8;

// ── Ícones SVG inline ─────────────────────────────────────────────────────────

const ChevronIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const XMarkIcon = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const CheckIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const SearchIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

// ── Componente ────────────────────────────────────────────────────────────────

/**
 * @param {object}   props
 * @param {Array}    props.procedures  - cadastro ativo: [{ Id, Label, Duration, Price }]
 * @param {Array}    props.value       - linhas escolhidas: [{ procedure_id, minutes, price }]
 * @param {function} props.onChange    - (lines) => void
 * @param {string}   [props.error]     - mensagem de validação abaixo do campo
 * @param {boolean}  [props.disabled]
 * @param {boolean}  [props.inline]    - lista no fluxo da página (dentro de um
 *                                       dialog com overflow escondido) em vez de
 *                                       flutuar sobre o conteúdo
 * @param {boolean}  [props.showTotals] - soma das linhas (duração e valor) abaixo
 *                                       delas, quando há mais de uma; o drawer
 *                                       não usa porque já mostra os campos de soma
 */
export default function ProcedureMultiSelect({
  procedures,
  value,
  onChange,
  error,
  disabled = false,
  inline = false,
  showTotals = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapperRef = useRef(null);
  const searchRef = useRef(null);

  const lines = useMemo(() => (Array.isArray(value) ? value : []), [value]);
  const catalog = useMemo(() => (Array.isArray(procedures) ? procedures : []), [procedures]);
  const showSearch = catalog.length > SEARCH_THRESHOLD;

  const byId = useMemo(() => {
    const map = new Map();
    catalog.forEach(p => map.set(String(p.Id), p));
    return map;
  }, [catalog]);

  const selectedIds = useMemo(() => new Set(lines.map(l => String(l.procedure_id))), [lines]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return catalog;
    return catalog.filter(p => String(p.Label || '').toLowerCase().includes(term));
  }, [catalog, search]);

  // ── Fecha ao clicar fora ou com Escape ──────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setIsOpen(false);
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && showSearch) searchRef.current?.focus();
    if (!isOpen) setSearch('');
  }, [isOpen, showSearch]);

  // ── Ações ───────────────────────────────────────────────────────────────────

  const toggle = (proc) => {
    const id = String(proc.Id);
    if (selectedIds.has(id)) {
      onChange(lines.filter(l => String(l.procedure_id) !== id));
    } else {
      onChange([...lines, lineFromProcedure(proc)]);
    }
  };

  const remove = (id) => onChange(lines.filter(l => String(l.procedure_id) !== String(id)));

  const updateLine = (id, patch) => {
    onChange(lines.map(l => (String(l.procedure_id) === String(id) ? { ...l, ...patch } : l)));
  };

  const openList = () => {
    if (!disabled) setIsOpen(open => !open);
  };

  const fieldClass = [
    styles.field,
    isOpen ? styles.fieldOpen : '',
    error ? styles.fieldError : '',
    disabled ? styles.fieldDisabled : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      {/* ── Campo com as etiquetas ─────────────────────────────────────────── */}
      <div
        className={fieldClass}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={openList}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openList();
          }
        }}
      >
        {lines.length === 0 ? (
          <span className={styles.placeholder}>Selecionar...</span>
        ) : (
          <span className={styles.chips}>
            {lines.map(line => {
              const proc = byId.get(String(line.procedure_id));
              return (
                <span key={line.procedure_id} className={styles.chip}>
                  <span className={styles.chipLabel}>{proc?.Label || 'Procedimento'}</span>
                  {!disabled && (
                    <button
                      type="button"
                      className={styles.chipRemove}
                      aria-label={`Remover ${proc?.Label || 'procedimento'}`}
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => { e.stopPropagation(); remove(line.procedure_id); }}
                    >
                      <XMarkIcon />
                    </button>
                  )}
                </span>
              );
            })}
          </span>
        )}
        <span className={styles.chevron}><ChevronIcon /></span>
      </div>

      {/* ── Lista ─────────────────────────────────────────────────────────── */}
      {isOpen && (
        <div
          className={`${styles.menu} ${inline ? styles.menuInline : ''}`}
          role="listbox"
          aria-multiselectable="true"
        >
          {showSearch && (
            <div className={styles.searchRow}>
              <span className={styles.searchIcon}><SearchIcon /></span>
              <input
                ref={searchRef}
                type="text"
                className={styles.searchInput}
                placeholder="Buscar procedimento"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          )}
          <div className={styles.options}>
            {filtered.map(proc => {
              const checked = selectedIds.has(String(proc.Id));
              const base = lineFromProcedure(proc);
              const meta = [
                base.minutes > 0 ? `${base.minutes} min` : null,
                base.price !== '' ? formatPrice(parseFloat(base.price)) : null,
              ].filter(Boolean).join(' · ');
              return (
                <button
                  key={proc.Id}
                  type="button"
                  role="option"
                  aria-selected={checked}
                  className={`${styles.option} ${checked ? styles.optionChecked : ''}`}
                  onClick={() => toggle(proc)}
                >
                  <span className={styles.checkbox}>{checked && <CheckIcon />}</span>
                  <span className={styles.optionLabel}>{proc.Label}</span>
                  {meta && <span className={styles.optionMeta}>{meta}</span>}
                </button>
              );
            })}
            {filtered.length === 0 && (
              <div className={styles.empty}>
                {catalog.length === 0 ? 'Nenhum procedimento cadastrado' : 'Nenhum procedimento encontrado'}
              </div>
            )}
          </div>
          <div className={styles.menuFooter}>
            <span className={styles.menuCount}>
              {lines.length === 0
                ? 'Nenhum selecionado'
                : lines.length === 1 ? '1 selecionado' : `${lines.length} selecionados`}
            </span>
            <button type="button" className={styles.menuDone} onClick={() => setIsOpen(false)}>
              Pronto
            </button>
          </div>
        </div>
      )}

      {error && <span className={styles.error}>{error}</span>}

      {/* ── Uma linha por procedimento: duração e valor ────────────────────── */}
      {lines.length > 0 && (
        <div className={styles.lines}>
          {lines.map(line => {
            const proc = byId.get(String(line.procedure_id));
            return (
              <div key={line.procedure_id} className={styles.line}>
                <span className={styles.lineName} title={proc?.Label || ''}>
                  {proc?.Label || 'Procedimento'}
                </span>
                <label className={styles.lineField}>
                  <input
                    type="number"
                    className={styles.lineInput}
                    value={line.minutes === '' ? '' : line.minutes}
                    onChange={(e) => updateLine(line.procedure_id, {
                      minutes: e.target.value === '' ? '' : Math.max(0, parseInt(e.target.value, 10) || 0),
                    })}
                    min="0"
                    step="5"
                    disabled={disabled}
                    aria-label={`Duração de ${proc?.Label || 'procedimento'} em minutos`}
                  />
                  <span className={styles.lineSuffix}>min</span>
                </label>
                <label className={`${styles.lineField} ${styles.lineFieldPrice}`}>
                  <span className={styles.linePrefix}>R$</span>
                  <input
                    type="number"
                    className={`${styles.lineInput} ${styles.lineInputPrice}`}
                    value={line.price}
                    onChange={(e) => updateLine(line.procedure_id, { price: e.target.value })}
                    placeholder="0,00"
                    min="0"
                    step="0.01"
                    disabled={disabled}
                    aria-label={`Valor de ${proc?.Label || 'procedimento'}`}
                  />
                </label>
              </div>
            );
          })}
          {showTotals && lines.length > 1 && (() => {
            const totals = linesTotals(lines);
            return (
              <div className={styles.totals}>
                <span className={styles.totalsLabel}>Total</span>
                <span className={styles.totalsValue}>{totals.minutes} min</span>
                <span className={styles.totalsValue}>{formatPrice(totals.price)}</span>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}
